/**
 * Effective hours and pay for an assignment — the frontend twin of
 * backend/src/utils/assignmentHours.ts.
 *
 * Hours default to the event's crew-call-to-end span when none are entered,
 * and pay follows from the effective rate. The backend applies the same rule
 * in every report; this lets the event page (which reads raw assignment
 * rows) show the same numbers. An end time stored earlier than call is one
 * past midnight and rolls a day forward.
 */

import type { RateType } from '../types/models';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface EventSpan {
  callTime?: string | null;
  endTime?: string | null;
}

/** Crew call → end, in hours (2 dp), or null when either time is unset. */
export function scheduledHours(event: EventSpan): number | null {
  if (!event.callTime || !event.endTime) return null;
  const call = new Date(event.callTime).getTime();
  const end = new Date(event.endTime).getTime();
  if (Number.isNaN(call) || Number.isNaN(end)) return null;
  let ms = end - call;
  if (ms <= 0) ms += DAY_MS;
  if (ms <= 0) return null;
  return Math.round((ms / 3_600_000) * 100) / 100;
}

export function effectiveHours(
  assignment: { hoursWorked?: string | number | null },
  event: EventSpan
): { hours: number | null; defaulted: boolean } {
  if (assignment.hoursWorked !== null && assignment.hoursWorked !== undefined && assignment.hoursWorked !== '') {
    return { hours: Number(assignment.hoursWorked), defaulted: false };
  }
  const hours = scheduledHours(event);
  return { hours, defaulted: hours !== null };
}

export function effectivePay(
  assignment: {
    totalPay?: number | null;
    effectiveRate?: number | null;
    effectiveRateType?: RateType | null;
  },
  hours: number | null
): number | null {
  if (assignment.totalPay !== null && assignment.totalPay !== undefined) return assignment.totalPay;
  const rate = assignment.effectiveRate;
  if (rate === null || rate === undefined || !assignment.effectiveRateType) return null;
  if (assignment.effectiveRateType === 'FLAT') return rate;
  if (hours === null) return null;
  return Math.round(rate * hours);
}
